import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import T from "../styles/tokens";
import { Badge, Btn, StatCard, Spinner, Ic, fmtDate, timeAgo } from "../components/UI";
import * as api from "../utils/api";

export default function Dashboard() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [c, a] = await Promise.all([
          api.listClients(),
          api.getAuditLog({ limit: 8 }),
        ]);
        setClients(c);
        setAudit(a);
      } catch (err) {
        console.error("Dashboard load failed:", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
        <Spinner size={36} />
      </div>
    );
  }

  const connected = clients.filter((c) => c.status === "connected");
  const pending = clients.filter((c) => c.status === "pending");
  const healthy = connected.filter((c) => c.healthStatus === "healthy");

  return (
    <div style={{ animation: "pfFadeIn 0.3s ease-out" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: 0 }}>Dashboard</h1>
          <p style={{ fontSize: 13, color: T.gray400, fontFamily: T.font, margin: "4px 0 0" }}>Cross-tenant administration overview</p>
        </div>
        <Btn onClick={() => navigate("/dashboard/invite")} icon={<Ic.Plus />}>New Invitation</Btn>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 24 }}>
        <StatCard label="Active Clients" value={connected.length} accent={T.green} sub={`${healthy.length} healthy`} />
        <StatCard label="Pending Invitations" value={pending.length} accent={T.amber} sub={pending.length > 0 ? "Awaiting consent" : "All clear"} />
        <StatCard label="Total Managed" value={clients.length} sub={`${clients.filter((c) => c.status === "revoked").length} revoked`} />
      </div>

      {/* Client Table */}
      <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, marginBottom: 24 }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.gray200}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: 0 }}>Clients</h3>
          <Btn variant="ghost" size="sm" onClick={() => navigate("/dashboard/clients")}>View All</Btn>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: T.font }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.gray100}` }}>
                {["Company", "Contact", "Template", "Status", "Connected"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 16px", fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.slice(0, 6).map((c) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/dashboard/clients/${c.id}`)}
                  style={{ borderBottom: `1px solid ${T.gray100}`, cursor: "pointer", transition: "background 0.1s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = T.gray100)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td style={{ padding: "12px 16px", fontWeight: 600, color: T.indigo }}>{c.name}</td>
                  <td style={{ padding: "12px 16px", color: T.charcoal }}>{c.contactName || c.contactEmail}</td>
                  <td style={{ padding: "12px 16px", color: T.charcoal }}>{c.templateId}</td>
                  <td style={{ padding: "12px 16px" }}><Badge status={c.status} /></td>
                  <td style={{ padding: "12px 16px", color: T.gray400, fontSize: 12 }}>{fmtDate(c.connectedAt)}</td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 32, textAlign: "center", color: T.gray400, fontStyle: "italic" }}>
                    No clients onboarded yet. Create your first invitation to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}` }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.gray200}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: 0 }}>Recent Activity</h3>
          <Btn variant="ghost" size="sm" onClick={() => navigate("/dashboard/audit")}>Full Log</Btn>
        </div>
        <div style={{ padding: "4px 0" }}>
          {audit.map((e) => (
            <div key={e.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 20px", borderBottom: `1px solid ${T.gray100}` }}>
              <Badge status={e.severity} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: T.charcoal, fontFamily: T.font }}>{e.detail}</div>
                <div style={{ fontSize: 11, color: T.gray400, marginTop: 3, fontFamily: T.font }}>{e.clientName} • {e.actor} • {timeAgo(e.timestamp)}</div>
              </div>
            </div>
          ))}
          {audit.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: T.gray400, fontStyle: "italic" }}>No activity recorded yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
